import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Mail, Download } from 'lucide-react';

export default function Hero({ greeting, socialMediaLinks, contactEmail = 'khushalgondaliya020@gmail.com' }) {
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 10 },
    },
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center px-4 pt-20">
      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        className="text-center max-w-4xl"
      >
        {/* Profile Image with animated border */}
        <motion.div
          variants={item}
          className="mb-8 relative inline-block"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 rounded-full blur-xl opacity-75"
          />
          <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 p-1 flex items-center justify-center">
            <div className="w-full h-full rounded-full bg-white dark:bg-slate-950 flex items-center justify-center text-5xl font-bold gradient-text">
              KG
            </div>
          </div>
        </motion.div>

        {/* Name */}
        <motion.h1
          variants={item}
          className="text-5xl md:text-7xl font-bold mb-4"
        >
          {greeting.username.split(' ')[0]}
          <span className="gradient-text"> {greeting.username.split(' ')[1]}</span>
        </motion.h1>

        {/* Title */}
        <motion.h2
          variants={item}
          className="text-3xl md:text-4xl font-bold mb-6 text-gray-600 dark:text-gray-400"
        >
          {greeting.title}
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          variants={item}
          className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto"
        >
          {greeting.subTitle}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          variants={item}
          className="flex flex-wrap gap-4 justify-center mb-12"
        >
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href={greeting.resumeLink}
            download
            className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg font-semibold flex items-center gap-2 hover:shadow-lg"
          >
            <Download size={20} />
            Download Resume
          </motion.a>
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href={`mailto:${contactEmail}`}
            className="px-8 py-3 border-2 border-primary-500 text-primary-500 dark:text-primary-400 rounded-lg font-semibold hover:bg-primary-50 dark:hover:bg-slate-800 transition-colors"
          >
            Get in Touch
          </motion.a>
        </motion.div>

        {/* Social Links */}
        <motion.div
          variants={item}
          className="flex justify-center gap-6"
        >
          {socialMediaLinks.github && (
            <motion.a
              whileHover={{ scale: 1.2, rotate: 12 }}
              href={socialMediaLinks.github}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800"
            >
              <Github size={28} />
            </motion.a>
          )}
          {socialMediaLinks.linkedin && (
            <motion.a
              whileHover={{ scale: 1.2, rotate: 12 }}
              href={socialMediaLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800"
            >
              <Linkedin size={28} />
            </motion.a>
          )}
          {socialMediaLinks.gmail && (
            <motion.a
              whileHover={{ scale: 1.2, rotate: 12 }}
              href={`mailto:${socialMediaLinks.gmail}`}
              className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800"
            >
              <Mail size={28} />
            </motion.a>
          )}
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="mt-16"
        >
          <p className="text-gray-500 dark:text-gray-400 mb-2">Scroll to explore</p>
          <div className="flex justify-center">
            <div className="w-6 h-10 border-2 border-primary-500 rounded-full flex items-center justify-center">
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-1 h-3 bg-primary-500 rounded-full"
              />
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Background decorations */}
      <div className="fixed top-20 right-10 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob dark:opacity-10" />
      <div className="fixed bottom-0 left-10 w-96 h-96 bg-secondary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob dark:opacity-10 animation-delay-2s" />
    </section>
  );
}
